import { useEffect } from "react";
import { useLocation } from "react-router-dom";

// Global scroll-reveal: scans the page for sections / cards / grid items
// and reveals them with a staggered fade-up as they enter the viewport.
// No per-screen edits needed — it targets CSS-module classes by substring.
const SELECTORS = [
    '[class*="section"]',
    '[class*="card"]',
    '[class*="item"]',
    '[class*="preview"]',
    '[class*="col"]',
    '[class*="box"]',
    '[class*="user"]',
    '[class*="head"]',
    '[class*="title"]',
    '[class*="stage"]',
].join(",");

const ScrollReveal = () => {
    const { pathname } = useLocation();

    useEffect(() => {
        const reduce = window.matchMedia(
            "(prefers-reduced-motion: reduce)"
        ).matches;
        if (reduce) return;

        let io;
        let raf;

        const setup = () => {
            const els = Array.from(document.querySelectorAll(SELECTORS)).filter(
                (el) =>
                    !el.dataset.reveal &&
                    !el.closest("header") &&
                    el.offsetParent !== null
            );

            // stagger siblings sharing the same parent
            const counters = new Map();
            els.forEach((el) => {
                el.dataset.reveal = "hidden";
                const p = el.parentElement;
                const n = counters.get(p) || 0;
                counters.set(p, n + 1);
                el.style.setProperty(
                    "--reveal-delay",
                    `${Math.min(n, 8) * 70}ms`
                );
            });

            io = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting) {
                            entry.target.dataset.reveal = "in";
                            io.unobserve(entry.target);
                        }
                    });
                },
                { threshold: 0.08, rootMargin: "0px 0px -8% 0px" }
            );

            els.forEach((el) => io.observe(el));
        };

        // wait a tick for the page transition to mount its content
        raf = window.setTimeout(setup, 120);

        return () => {
            window.clearTimeout(raf);
            if (io) io.disconnect();
        };
    }, [pathname]);

    return null;
};

export default ScrollReveal;
